import React, { useState } from 'react'
import { Plus, Edit2, Trash2, GripVertical, Calendar } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useQueues, useCreateQueue, useUpdateQueue, useDeleteQueue, useProfiles, usePosts } from '@/hooks/useSocialMedia'
import type { Queue } from '@/hooks/useSocialMedia'

export default function QueueManager() {
  const { data: queues = [], isLoading } = useQueues()
  const { data: profiles = [] } = useProfiles()
  const createQueue = useCreateQueue()
  const updateQueue = useUpdateQueue()
  const deleteQueue = useDeleteQueue()
  
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingQueue, setEditingQueue] = useState<Queue | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    profile_id: '',
    sort_order: 'date_asc' as const
  })

  const handleCreateOrUpdate = async () => {
    if (editingQueue) {
      await updateQueue.mutateAsync({ id: editingQueue.id, ...formData })
    } else {
      await createQueue.mutateAsync(formData)
    }
    setShowCreateDialog(false)
    setEditingQueue(null)
    resetForm()
  }

  const handleEdit = (queue: Queue) => {
    setEditingQueue(queue)
    setFormData({
      name: queue.name,
      description: queue.description || '',
      profile_id: queue.profile_id || '',
      sort_order: queue.sort_order
    })
    setShowCreateDialog(true)
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this queue?')) {
      await deleteQueue.mutateAsync(id)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      profile_id: '',
      sort_order: 'date_asc'
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Content Queues</h2>
          <p className="text-muted-foreground">Organize your posts into queues for better scheduling</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Queue
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading queues...</p>
        </div>
      ) : queues.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground mb-4">No queues created yet</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              Create your first queue
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {queues.map((queue) => {
            const profile = profiles.find(p => p.id === queue.profile_id)
            
            return (
              <QueueCard
                key={queue.id}
                queue={queue}
                profile={profile}
                onEdit={() => handleEdit(queue)}
                onDelete={() => handleDelete(queue.id)}
              />
            )
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingQueue ? 'Edit Queue' : 'Create New Queue'}</DialogTitle>
            <DialogDescription>
              Queues help you organize and schedule your social media posts
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Queue Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Monday Morning Posts"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this queue is for..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="profile">Associated Profile</Label>
              <Select
                value={formData.profile_id}
                onValueChange={(value) => setFormData({ ...formData, profile_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a profile (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No specific profile</SelectItem>
                  {profiles.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.name} ({profile.platform})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sort_order">Sort Order</Label>
              <Select
                value={formData.sort_order}
                onValueChange={(value: any) => setFormData({ ...formData, sort_order: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_asc">Date (oldest first)</SelectItem>
                  <SelectItem value="date_desc">Date (newest first)</SelectItem>
                  <SelectItem value="custom">Custom order</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => {
                setShowCreateDialog(false)
                setEditingQueue(null)
                resetForm()
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateOrUpdate}
                disabled={!formData.name || createQueue.isPending || updateQueue.isPending}
              >
                {editingQueue ? 'Update' : 'Create'} Queue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface QueueCardProps {
  queue: Queue
  profile?: any
  onEdit: () => void
  onDelete: () => void
}

function QueueCard({ queue, profile, onEdit, onDelete }: QueueCardProps) {
  const { data: posts = [] } = usePosts({ profiles: queue.profile_id ? [queue.profile_id] : undefined })
  const queuePosts = posts.filter(p => p.queue_id === queue.id)
  
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{queue.name}</CardTitle>
            {queue.description && (
              <CardDescription className="mt-1">{queue.description}</CardDescription>
            )}
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {profile && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {profile.platform}
              </Badge>
              <span className="text-sm text-muted-foreground">{profile.name}</span>
            </div>
          )}
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Posts in queue</span>
            <span className="font-medium">{queuePosts.length}</span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Sort order</span>
            <Badge variant="outline" className="text-xs">
              {queue.sort_order.replace('_', ' ')}
            </Badge>
          </div>
          
          {queuePosts.length > 0 && (
            <div className="pt-3 border-t">
              <p className="text-xs text-muted-foreground mb-2">Next posts:</p>
              <div className="space-y-1">
                {queuePosts.slice(0, 2).map((post) => (
                  <div key={post.id} className="flex items-center gap-2 text-sm">
                    <Calendar className="h-3 w-3 text-muted-foreground" />
                    <span className="truncate flex-1">
                      {post.caption || post.content || 'Untitled post'}
                    </span>
                  </div>
                ))}
                {queuePosts.length > 2 && (
                  <p className="text-xs text-muted-foreground">
                    +{queuePosts.length - 2} more posts
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}